import { CloudOff, LucideIcon, WifiOff } from 'lucide-react';

import { Banner } from '@crossnokaye/ux-common';

import { AgentStatusVariant, useAgentStatusDisplay } from '@/hooks/use-agent-status-display';
import { Icon } from '@/ux-common/components/icon';

type BannerVariantConfig = {
  bannerTone: 'warning' | 'default';
  icon: LucideIcon;
  title: string;
  body: string;
};

const BANNER_VARIANTS: Record<AgentStatusVariant, BannerVariantConfig> = {
  offline: {
    bannerTone: 'warning',
    icon: WifiOff,
    title: 'Offline Mode:',
    body: 'The facility is not connected to the cloud. Use the on-site HMI to monitor or control.',
  },
  unentitled: {
    bannerTone: 'default',
    icon: CloudOff,
    title: 'Access Restricted:',
    body: 'The facility is not entitled for remote access. Use the on-site HMI to monitor or control.',
  },
};

/**
 * A banner that appears when the current facility's agent reports an actionable
 * non-online status.
 *
 * Behavior:
 * - Appears when agent presence reports the current facility as offline
 * - Supports variant-driven copy/styling as new statuses are introduced
 * - Auto-dismisses when agent comes back online
 */
export const AgentStatusBanner = () => {
  const { bannerVariant } = useAgentStatusDisplay();

  if (!bannerVariant) {
    return null;
  }

  const variant = BANNER_VARIANTS[bannerVariant];
  const Glyph = variant.icon;

  return (
    <Banner variant={variant.bannerTone}>
      <div className="flex items-center justify-center gap-3">
        <Icon className="icon-sm bg-ckds-base-black">
          <Glyph className="glyph glyph-sm" aria-hidden="true" />
        </Icon>
        <span className="truncate text-ckds-base-black">
          <span className="font-bold">{variant.title}</span> {variant.body}
        </span>
      </div>
    </Banner>
  );
};
